import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context/AppContext";
import { auth as authApi } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";

/**
 * Blocking modal shown when the current user has `must_change_password=true`.
 * The account got a temporary password from an admin — the user MUST pick
 * their own before doing anything else. Only escape hatch is Log out.
 */
export default function ForceChangePasswordModal() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useApp();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pw1.trim().length < 6) {
      toast.error(t("force_pw.too_short", "La contraseña debe tener al menos 6 caracteres"));
      return;
    }
    if (pw1 !== pw2) {
      toast.error(t("force_pw.mismatch", "Las contraseñas no coinciden"));
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(pw1.trim());
      // Clear the flag locally so the modal closes without a full round-trip.
      setUser({ ...user, must_change_password: false });
      toast.success(t("force_pw.updated", "Contraseña actualizada"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("force_pw.error", "No se pudo actualizar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* blocking — do nothing */ }}>
      <DialogContent
        className="sm:max-w-[440px]"
        // Prevent closing via ESC / outside click. Only path out is Log out.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-pw-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} className="text-amber-600" />
            {t("force_pw.title", "Elige tu nueva contraseña")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "force_pw.subtitle",
              "Un administrador reinició tu acceso con una contraseña temporal. Por seguridad, escoge tu propia contraseña antes de continuar."
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="canales-overline">{t("force_pw.new", "Nueva contraseña")}</label>
            <Input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              autoFocus
              autoComplete="new-password"
              minLength={6}
              className="mt-1"
              data-testid="force-pw-new"
            />
          </div>
          <div>
            <label className="canales-overline">{t("force_pw.confirm", "Confirmar contraseña")}</label>
            <Input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              className="mt-1"
              data-testid="force-pw-confirm"
            />
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Button
              type="submit"
              disabled={busy || !pw1 || !pw2}
              className="brand-button"
              data-testid="force-pw-submit"
            >
              {busy ? t("common.loading") : t("force_pw.submit", "Actualizar contraseña")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={logout}
              disabled={busy}
              className="gap-1.5 text-stone-600"
              data-testid="force-pw-logout"
            >
              <LogOut size={14} />
              {t("force_pw.logout", "Cerrar sesión")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
